import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createRangePatterns, createUnitPatterns, createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['nest', 'minimal', 'neon', 'industrial', 'retro'] as const;
const VALID_MODES = ['auto', 'heat', 'cool', 'fan', 'off'];

const THEME_ALIASES: Record<string, string> = {
  'google': 'nest',
  'google-nest': 'nest',
  'clean': 'minimal',
  'simple': 'minimal',
  'glow': 'neon',
  'cyberpunk': 'neon',
  'factory': 'industrial',
  'gauge': 'industrial',
  'vintage': 'retro',
  'classic': 'retro',
};

export class ThermostatCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Range patterns (min, max, step, value, range X to Y)
    // Remap "value" to "targetTemperature" for thermostat
    for (const [regex, handler] of createRangePatterns()) {
      if (regex.source.includes('value')) {
        this.commandPatterns.set(regex, (match, component) => {
          const result = handler(match, component);
          if (result?.properties?.value != null) {
            return { properties: { targetTemperature: result.properties.value } };
          }
          return result;
        });
      } else {
        this.commandPatterns.set(regex, handler);
      }
    }

    // Unit patterns (celsius, fahrenheit)
    for (const [regex, handler] of createUnitPatterns()) {
      this.commandPatterns.set(regex, handler);
    }

    // Theme patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Color patterns
    for (const [regex, handler] of createColorPatterns(['trackColor', 'coolColor', 'heatColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // --- Custom Thermostat patterns ---

    // "set target to 72" / "target 72"
    this.commandPatterns.set(
      /(?:set )?(?:target|target temp(?:erature)?) (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { targetTemperature: parseFloat(match[1]) } })
    );

    // "mode cool" / "set mode to heat"
    this.commandPatterns.set(
      /(?:set )?(?:mode|hvac) (?:to )?(\w+)/i,
      (match) => {
        const mode = match[1].toLowerCase();
        if (!VALID_MODES.includes(mode)) return null;
        return { properties: { mode } };
      }
    );

    // "switch to cool/heat/auto/fan/off"
    this.commandPatterns.set(
      /switch (?:to |mode )?(auto|heat|cool|fan|off)/i,
      (match) => ({ properties: { mode: match[1].toLowerCase() } })
    );

    // "show humidity" / "hide humidity"
    this.commandPatterns.set(
      /(?:show|display|enable) humidity/i,
      () => ({ properties: { showHumidity: true } })
    );
    this.commandPatterns.set(
      /(?:hide|remove|disable) humidity/i,
      () => ({ properties: { showHumidity: false } })
    );

    // "set humidity to 45"
    this.commandPatterns.set(
      /(?:set )?humidity (?:to |= ?)?(\d+)/i,
      (match) => ({ properties: { humidity: parseInt(match[1], 10) } })
    );

    // "set temperature to 72" (current/display temperature)
    this.commandPatterns.set(
      /(?:set )?(?:current )?temp(?:erature)? (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { temperature: parseFloat(match[1]) } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.THERMOSTAT && this.hasKeywords(command);
  }

  private hasKeywords(command: string): boolean {
    const keywords = [
      "target", "temperature", "temp", "mode", "hvac",
      "celsius", "fahrenheit", "humidity", "theme",
      "min", "max", "range", "heat", "cool", "auto", "fan",
      "nest", "neon", "industrial", "retro", "minimal",
      "track color", "cool color", "heat color",
      "show", "hide", "switch", "unit",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
      if (patternMatch?.style) {
        changes.style = { ...changes.style, ...patternMatch.style };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: `Could not parse thermostat command: "${command}"`,
        suggestions: await this.getExampleCommands(ComponentType.THERMOSTAT),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    const props = (component.properties || {}) as Record<string, unknown>;
    const min = (changes.properties?.min as number) ?? (props.min as number) ?? 50;
    const max = (changes.properties?.max as number) ?? (props.max as number) ?? 90;

    if (min >= max) {
      return { valid: false, error: `Min (${min}) must be less than max (${max})` };
    }

    if (changes.properties?.targetTemperature != null) {
      const target = changes.properties.targetTemperature as number;
      if (target < min || target > max) {
        return { valid: false, error: `Target temperature ${target} is outside range ${min}-${max}` };
      }
    }

    if (changes.properties?.mode != null && !VALID_MODES.includes(changes.properties.mode as string)) {
      return { valid: false, error: `Invalid mode "${changes.properties.mode}". Valid: ${VALID_MODES.join(', ')}` };
    }

    if (changes.properties?.humidity != null) {
      const h = changes.properties.humidity as number;
      if (h < 0 || h > 100) {
        return { valid: false, error: "Humidity must be between 0 and 100" };
      }
    }

    return super.validateChanges(changes, component);
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set target to 72",
      "Mode cool",
      "Mode heat",
      "Switch to auto",
      "Use celsius",
      "Show humidity",
      "Theme nest",
      "Theme neon",
      "Range 40 to 100",
      "Track color #333",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;

    const target = (props.targetTemperature as number) ?? 72;
    suggestions.push(`Set target to ${target + 2}`);
    suggestions.push(`Set target to ${target - 2}`);

    const currentMode = (props.mode as string) ?? 'auto';
    const otherModes = VALID_MODES.filter(m => m !== currentMode);
    if (otherModes.length > 0) {
      suggestions.push(`Mode ${otherModes[0]}`);
    }

    const unit = (props.unit as string) ?? 'fahrenheit';
    suggestions.push(`Use ${unit === 'fahrenheit' ? 'celsius' : 'fahrenheit'}`);

    const showHumidity = props.showHumidity as boolean;
    suggestions.push(showHumidity !== false ? 'Hide humidity' : 'Show humidity');

    const currentTheme = (props.theme as string) ?? 'nest';
    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    return suggestions.slice(0, 8);
  }
}

export const thermostatCommandHandler = new ThermostatCommandHandler();
